// © 2015-2021 WDNA - All Rights Reserved
import _ from 'lodash';

import { UserDO, RolePermissionDO } from 'wdna-components';

import Auth from '../auth/Auth';
import { authorization } from './requests';

export const REQUEST_USER: string = 'REQUEST_USER';
export const USER_INFO: string = 'USER_INFO';
export const REMOVE_USER: string = 'REMOVE_USER';

export type RxAction = {
  type: string;
  data?: UserDO;
}

/**
 * Remove user info from store
 */
export function removeUserInfo(): void {
  window.store.dispatch({
    type: REMOVE_USER,
  });
}

/**
 * Checks if current user has a rol
 *
 * @param roleStr name of the rol to check
 * @returns true if user has role false otherwise
 */
export function hasRole(roleStr: string): boolean {
  const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
  if (!user || !user.role) return false;

  const { role } = user;
  return roleStr === role.name;
}

/**
 * Checks if current user has a permission
 *
 * @param permissionStr name of the permission to check
 * @returns true if user has permission false otherwise
 */
export function hasPermission(permissionStr: string): boolean {
  const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
  if (!user || !user.role) return false;

  let result: boolean = false;

  const { role } = user;
  const permissions: RolePermissionDO[] = role.role_permissions || [];
  if (permissions) {
    permissions.forEach((elem: RolePermissionDO) => {
      const p: string = elem?.permission?.permission || '';
      result = result || p === permissionStr;
    });
  }

  return result;
}

/**
 * Check if current user has one of the list permissions
 *
 * @param permissionsList list of permissions to check
 * @returns true if user has permission false otherwise
 */
export function hasOnePermission(permissionsList: any[]): boolean {
  const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
  if (!user || !user.role || !permissionsList) return false;

  let result: boolean = false;

  const { role } = user;
  const permissions: RolePermissionDO[] = role.role_permissions || [];
  if (permissions) {
    permissions.forEach((elem: RolePermissionDO) => {
      const p: string = elem?.permission?.permission || '';
      result = result || permissionsList.indexOf(p) !== -1;
    });
  }

  return result;
}

/**
 * Gets current user information from backend
 *
 * @returns user object information
 */
export function getLoggedUserInfo(): Promise<UserDO> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };
  return new Promise((resolve: ((value: UserDO) => void), reject: ((reason?: any) => void)) => {
    authorization<UserDO>('myinfo', {}, options)
      ?.then((resp: UserDO) => {
        resolve(resp);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Gets current user information from backend
 *
 * @returns user object information
 */
export function getRefreshUserInfo(): Promise<UserDO> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };
  return new Promise((resolve: ((value: UserDO) => void), reject: ((reason?: any) => void)) => {
    authorization<UserDO>('myinfo', { refresh: true }, options)
      ?.then((resp: UserDO) => {
        resolve(resp);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Sets the user informtion into database
 *
 * @param data user information to update
 * @returns message with result of the operation
 */
export function updateUserData(data: any): Promise<any> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };

  if (data?.user?.area_id) data.user.area = { id: data.user.area_id };

  return new Promise((resolve: ((value: unknown) => void), reject: ((reason?: any) => void)) => {
    authorization('set', data, options)
      ?.then(() => {
        resolve('ok');
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Gets the list of areas supported by the system to update user information
 *
 * @returns list of areas into the system
 */
export function getAllAreas(): Promise<any> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };
  return new Promise((resolve: ((value: unknown) => void), reject: ((reason?: any) => void)) => {
    authorization('areas', {}, options)
      ?.then((resp: any) => {
        resolve(resp);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Gets the list of deparments supported by the system to update user information
 *
 * @returns list of departments into the system
 */
export function getAllDepartments(): Promise<any> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };
  return new Promise((resolve: ((value: unknown) => void), reject: ((reason?: any) => void)) => {
    authorization('departments', {}, options)
      ?.then((resp: any) => {
        resolve(resp);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Gets the list of applications supported by the system to update user information
 *
 * @returns list of applications into the system
 */
export function getAllApps(): Promise<any> {
  const options: Record<string, any> = { headers: { token: Auth.getToken() } };
  return new Promise((resolve: ((value: unknown) => void), reject: ((reason?: any) => void)) => {
    authorization('apps', {}, options)
      ?.then((resp: any) => {
        resolve(resp);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Updates user info into store system
 *
 * @param user user object
 * @returns promise with the user info
 */
export function updateUserInfo(user: any): Promise<any> {
  // Important to access backend
  window.api.setToken(Auth.getToken() || '');
  if (!user) {
    return new Promise((resolve: ((value: unknown) => void)) => {
      getLoggedUserInfo()
        .then((resp: any) => {
          window.store.dispatch({
            type: USER_INFO,
            data: { ...resp, picture: 'assets/img/avatars/sunny.png' },
          });
          return resolve(user);
        })
        .catch(() => {
          removeUserInfo();
        });
    });
  }

  return new Promise((resolve: ((value: unknown) => void)) => {
    window.store.dispatch({
      type: USER_INFO,
      data: { ...user, picture: 'assets/img/avatars/sunny.png' },
    });
    return resolve(user);
  });
}
